// src/data/encounters.ts — Tablas de encuentro por zona/fase horaria (Fase 3)
import type { ZoneId } from './gameConfig.js';
import type { AsteriaSpecies } from '../entities/asteria.js';
import { ASTERIAS } from './asterias.js';

export type EncounterEntry = {
  speciesId: string;
  weight: number; // peso relativo dentro de la tabla
  levelMin: number;
  levelMax: number;
  condition?: (ctx: EncounterContext)=> boolean;
};

export type EncounterContext = {
  zone: ZoneId;
  phase: 'amanecer'|'dia'|'atardecer'|'noche';
  tileKind?: string;
  seenRare?: boolean; // futuro clima/misión
};

function pickSpecies(id:string): AsteriaSpecies { return ASTERIAS.find(s=>s.id===id)!; }

// Tabla base Ruta Aurelia — común + variante horaria
export const RUTA_ENCOUNTERS: Record<EncounterContext['phase'], EncounterEntry[]> = {
  amanecer: [
    { speciesId:'verdora', weight: 30, levelMin:3, levelMax:6 },
    { speciesId:'voltine', weight: 24, levelMin:3, levelMax:6 },
    { speciesId:'neritha', weight: 14, levelMin:4, levelMax:6 },
    { speciesId:'pyrraxis', weight: 12, levelMin:4, levelMax:7 },
    { speciesId:'geodra', weight: 10, levelMin:5, levelMax:7 },
    { speciesId:'umbrelle', weight: 10, levelMin:5, levelMax:8, condition: c=> c.phase==='amanecer' }, // rare dawn
  ],
  dia: [
    { speciesId:'verdora', weight: 34, levelMin:3, levelMax:6 },
    { speciesId:'voltine', weight: 22, levelMin:3, levelMax:6 },
    { speciesId:'pyrraxis', weight: 18, levelMin:4, levelMax:7 },
    { speciesId:'neritha', weight: 12, levelMin:4, levelMax:6 },
    { speciesId:'geodra', weight: 12, levelMin:5, levelMax:7 },
    { speciesId:'umbrelle', weight: 2, levelMin:5, levelMax:8 }, // muy raro de día
  ],
  atardecer: [
    { speciesId:'verdora', weight: 22, levelMin:4, levelMax:7 },
    { speciesId:'voltine', weight: 20, levelMin:4, levelMax:7 },
    { speciesId:'neritha', weight: 18, levelMin:4, levelMax:7 },
    { speciesId:'pyrraxis', weight: 16, levelMin:4, levelMax:7 },
    { speciesId:'geodra', weight: 14, levelMin:5, levelMax:8 },
    { speciesId:'umbrelle', weight: 10, levelMin:5, levelMax:8 },
  ],
  noche: [
    { speciesId:'umbrelle', weight: 28, levelMin:5, levelMax:8 },
    { speciesId:'neritha', weight: 22, levelMin:4, levelMax:7 },
    { speciesId:'voltine', weight: 18, levelMin:4, levelMax:7 },
    { speciesId:'geodra', weight: 16, levelMin:5, levelMax:8 },
    { speciesId:'verdora', weight: 10, levelMin:4, levelMax:7 },
    { speciesId:'pyrraxis', weight: 6, levelMin:5, levelMax:8 },
  ],
};

// Utilidad: elegir aleatorio ponderado filtrado por condiciones
export function rollEncounter(zone: ZoneId, phase: EncounterContext['phase']): { speciesId:string; level:number } | null {
  if (zone !== 'ruta_aurelia') return null; // solo Ruta en Fase 3
  const table = RUTA_ENCOUNTERS[phase] ?? RUTA_ENCOUNTERS.dia;
  const ctx: EncounterContext = { zone, phase };
  const filtered = table.filter(e=> !e.condition || e.condition(ctx));
  const total = filtered.reduce((s,e)=>s+e.weight,0);
  let r = Math.random()*total;
  for(const e of filtered){
    r -= e.weight;
    if(r <= 0){
      const lvl = e.levelMin + Math.floor(Math.random()*(e.levelMax - e.levelMin +1));
      return { speciesId: e.speciesId, level: lvl };
    }
  }
  return null;
}

// Para Asterias visibles (overworld spawns) — elige 1-2 especies según fase, con comportamiento
export function pickVisibleSpecies(phase: EncounterContext['phase'], count=1): {speciesId:string; level:number}[] {
  const out: {speciesId:string; level:number}[] = [];
  for(let i=0;i<count;i++){
    const r = rollEncounter('ruta_aurelia', phase);
    if(r) out.push(r);
  }
  return out;
}
